import React from 'react';
import { Box, Stack } from '@mui/material';
import { motion } from 'framer-motion';

// ----------------------------------------------------------------------
// EditorialSection — a magazine-style band used between PageHero and the
// footer. Provides a consistent gutter, mono kicker, large display
// headline, optional italic accent, optional intro paragraph, and a
// brand-rule under the title block.
// ----------------------------------------------------------------------
export default function EditorialSection({
  kicker,
  title,
  titleAccent,
  intro,
  background = 'var(--paper)',
  textColor = 'var(--text)',
  align = 'left',
  py,
  children,
  id,
  watermark,
}) {
  const center = align === 'center';
  return (
    <Box
      component="section"
      id={id}
      sx={{
        position: 'relative',
        background,
        color: textColor,
        overflow: 'hidden',
        py: py ?? { xs: 10, md: 14 },
      }}
    >
      {watermark && (
        <Box
          aria-hidden
          sx={{
            position: 'absolute',
            right: '-4%',
            bottom: '-22%',
            fontFamily: 'var(--font-hebrew)',
            fontSize: 'min(60vw, 760px)',
            color: 'var(--navy-500)',
            opacity: 0.04,
            lineHeight: 0.8,
            pointerEvents: 'none',
            userSelect: 'none',
          }}
        >
          {watermark}
        </Box>
      )}

      <Box
        sx={{
          position: 'relative',
          maxWidth: 1280,
          mx: 'auto',
          px: { xs: 3, md: 6 },
        }}
      >
        {(kicker || title || intro) && (
          <Stack
            spacing={3}
            alignItems={center ? 'center' : 'flex-start'}
            sx={{
              mb: { xs: 6, md: 8 },
              textAlign: center ? 'center' : 'left',
              maxWidth: center ? 920 : 'none',
              mx: center ? 'auto' : 0,
            }}
          >
            {kicker && (
              <motion.div
                initial={{ opacity: 0, y: 12 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, margin: '-80px' }}
                transition={{ duration: 0.8, ease: [0.22, 1, 0.36, 1] }}
                style={{
                  fontFamily: 'var(--font-mono)',
                  // 11px at 0.22em tracking was legible only if you went
                  // looking for it. Bumped size/weight and pulled the
                  // tracking in — wide tracking on small type is what was
                  // actually costing the legibility, not the size alone.
                  fontSize: 12.5,
                  fontWeight: 700,
                  letterSpacing: '0.15em',
                  textTransform: 'uppercase',
                  color: 'var(--teal-500)',
                }}
              >
                {kicker}
              </motion.div>
            )}

            {title && (
              <motion.h2
                initial={{ opacity: 0, y: 24 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, margin: '-80px' }}
                transition={{ duration: 1, ease: [0.22, 1, 0.36, 1] }}
                style={{
                  margin: 0,
                  fontFamily: 'var(--font-display)',
                  fontWeight: 700,
                  lineHeight: 0.98,
                  letterSpacing: '-0.018em',
                  color: 'inherit',
                  fontSize: 'clamp(34px, 5.6vw, 76px)',
                  maxWidth: 980,
                }}
              >
                {title}
                {titleAccent && (
                  <>
                    {' '}
                    <span
                      style={{
                        fontStyle: 'italic',
                        fontWeight: 500,
                        color: 'var(--teal-500)',
                      }}
                    >
                      {titleAccent}
                    </span>
                  </>
                )}
              </motion.h2>
            )}

            {intro && (
              <motion.div
                initial={{ opacity: 0, y: 16 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, margin: '-80px' }}
                transition={{ duration: 0.9, delay: 0.15, ease: [0.22, 1, 0.36, 1] }}
                style={{
                  fontFamily: 'var(--font-body)',
                  fontWeight: 400,
                  fontSize: 'clamp(16px, 1.4vw, 19px)',
                  lineHeight: 1.6,
                  color: 'inherit',
                  opacity: 0.86,
                  maxWidth: 720,
                }}
              >
                {intro}
              </motion.div>
            )}

            <Box
              className="brand-rule"
              sx={{
                width: center ? 120 : 96,
                mt: 1,
              }}
            />
          </Stack>
        )}

        {children}
      </Box>
    </Box>
  );
}
